import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────

interface BarcodeScannerCameraProps {
  onScan: (code: string) => void;
  active: boolean;
}

type ScanEngine = 'native' | 'zxing' | null;

// ─── Native BarcodeDetector type augmentation ────────────────────

interface BarcodeDetectorResult {
  rawValue: string;
  format: string;
  boundingBox: DOMRectReadOnly;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats: string[] }) => {
      detect: (source: ImageBitmapSource) => Promise<BarcodeDetectorResult[]>;
    };
  }
}

// ─── Debounce helper to prevent duplicate reads ──────────────────

function useDebouncedCallback(callback: (code: string) => void, delayMs: number) {
  const lastScan = useRef<{ code: string; time: number }>({ code: '', time: 0 });

  return useCallback((code: string) => {
    const now = Date.now();
    if (code === lastScan.current.code && now - lastScan.current.time < delayMs) {
      return; // skip duplicate within debounce window
    }
    lastScan.current = { code, time: now };
    callback(code);
  }, [callback, delayMs]);
}

// ─── Component ──────────────────────────────────────────────────

export const BarcodeScannerCamera: React.FC<BarcodeScannerCameraProps> = ({ onScan, active }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number>(0);
  const zxingScannerRef = useRef<any>(null);

  const [engine, setEngine] = useState<ScanEngine>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const debouncedOnScan = useDebouncedCallback(onScan, 1500);

  // ─── Detect available engine on mount ──────────────────────
  useEffect(() => {
    if ('BarcodeDetector' in window && window.BarcodeDetector) {
      // Check if code_128 is supported
      (window.BarcodeDetector as any).getSupportedFormats?.()
        .then((formats: string[]) => {
          if (formats.includes('code_128')) {
            setEngine('native');
          } else {
            setEngine('zxing');
          }
        })
        .catch(() => setEngine('zxing'));
    } else {
      setEngine('zxing');
    }
  }, []);

  // ─── Start/stop scanning based on active + engine ──────────
  useEffect(() => {
    if (!active || !engine) return;

    let cancelled = false;

    const start = async () => {
      setLoading(true);
      setError(null);

      try {
        // Request high-resolution camera with continuous autofocus
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            // @ts-ignore - focusMode is valid on mobile browsers
            focusMode: 'continuous',
            // @ts-ignore
            focusDistance: { ideal: 0 },
          },
        });

        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;

        // Apply advanced constraints for autofocus (best-effort)
        const [videoTrack] = stream.getVideoTracks();
        try {
          await videoTrack.applyConstraints({
            // @ts-ignore - advanced constraints
            advanced: [{ focusMode: 'continuous' }],
          });
        } catch {
          // Not all devices support this — continue anyway
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        if (cancelled) return;
        setLoading(false);

        if (engine === 'native') {
          startNativeScan();
        } else {
          startZxingScan();
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoading(false);
          if (err.name === 'NotAllowedError') {
            setError('Permissão de câmera negada. Habilite nas configurações do navegador.');
          } else if (err.name === 'NotFoundError') {
            setError('Nenhuma câmera encontrada neste dispositivo.');
          } else {
            setError(`Erro ao acessar câmera: ${err.message || 'desconhecido'}`);
          }
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      stopScanning();
    };
  }, [active, engine]);

  // ─── Native BarcodeDetector scanning loop ──────────────────
  const startNativeScan = () => {
    if (!window.BarcodeDetector) return;

    const detector = new window.BarcodeDetector({ formats: ['code_128'] });

    const scan = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        animFrameRef.current = requestAnimationFrame(scan);
        return;
      }

      try {
        const results = await detector.detect(videoRef.current);
        if (results.length > 0) {
          const code = results[0].rawValue.trim();
          if (code) {
            debouncedOnScan(code);
          }
        }
      } catch {
        // Frame not ready — skip
      }

      // ~10 scans/sec for native (fast hardware decoder)
      setTimeout(() => {
        animFrameRef.current = requestAnimationFrame(scan);
      }, 100);
    };

    animFrameRef.current = requestAnimationFrame(scan);
  };

  // ─── ZXing fallback scanning loop ─────────────────────────
  const startZxingScan = async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');

      // Create a temporary container for Html5Qrcode (it needs a DOM element)
      const scannerId = `barcode-scanner-zxing-${Date.now()}`;
      const container = document.createElement('div');
      container.id = scannerId;
      container.style.display = 'none';
      document.body.appendChild(container);

      const html5Qrcode = new Html5Qrcode(scannerId, /* verbose= */ false);
      zxingScannerRef.current = { instance: html5Qrcode, container };

      // Use canvas-based frame extraction + decoding
      const scanLoop = () => {
        if (!videoRef.current || !canvasRef.current || videoRef.current.readyState < 2) {
          animFrameRef.current = requestAnimationFrame(scanLoop);
          return;
        }

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          animFrameRef.current = requestAnimationFrame(scanLoop);
          return;
        }

        // Use center crop for better barcode recognition
        const vw = video.videoWidth;
        const vh = video.videoHeight;

        // Crop center 80% width, 40% height (where guide box is)
        const cropW = Math.floor(vw * 0.8);
        const cropH = Math.floor(vh * 0.4);
        const cropX = Math.floor((vw - cropW) / 2);
        const cropY = Math.floor((vh - cropH) / 2);

        canvas.width = cropW;
        canvas.height = cropH;
        ctx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

        // Convert to file and scan
        canvas.toBlob(async (blob) => {
          if (!blob) {
            scheduleNext();
            return;
          }
          try {
            const file = new File([blob], 'frame.png', { type: 'image/png' });
            const result = await html5Qrcode.scanFileV2(file, /* showImage= */ false);
            if (result && result.decodedText) {
              const code = result.decodedText.trim();
              if (code) {
                debouncedOnScan(code);
              }
            }
          } catch {
            // No barcode found in this frame — normal
          }
          scheduleNext();
        }, 'image/png');
      };

      const scheduleNext = () => {
        // ~5 scans/sec for ZXing (software decoder — heavier)
        setTimeout(() => {
          animFrameRef.current = requestAnimationFrame(scanLoop);
        }, 200);
      };

      animFrameRef.current = requestAnimationFrame(scanLoop);
    } catch (err) {
      setError('Erro ao carregar biblioteca de leitura. Recarregue a página.');
    }
  };

  // ─── Stop all scanning ─────────────────────────────────────
  const stopScanning = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = 0;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (zxingScannerRef.current) {
      try {
        zxingScannerRef.current.instance?.clear?.();
        zxingScannerRef.current.container?.remove?.();
      } catch { /* ignore */ }
      zxingScannerRef.current = null;
    }
  };

  // ─── Render ────────────────────────────────────────────────
  if (error) {
    return (
      <div className="w-full max-w-md mx-auto p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <AlertTriangle size={24} className="text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-md mx-auto aspect-video bg-black rounded-lg overflow-hidden">
      {/* Hidden canvas for ZXing frame extraction */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Video feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center">
          <Loader2 size={32} className="text-emerald-400 animate-spin mb-2" />
          <p className="text-sm text-white">Iniciando câmera...</p>
        </div>
      )}

      {/* Guide box overlay */}
      {!loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {/* Darkened corners outside guide */}
          <div className="absolute inset-0 bg-black bg-opacity-30" />
          <div className="relative w-4/5 h-2/5 bg-transparent border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.3)] z-10">
            {/* Corner markers */}
            <div className="absolute -top-0.5 -left-0.5 w-5 h-5 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
            <div className="absolute -top-0.5 -right-0.5 w-5 h-5 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
            <div className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />
            {/* Scan line animation */}
            <div className="absolute left-2 right-2 h-0.5 bg-emerald-400 opacity-70 animate-pulse top-1/2 -translate-y-1/2" />
          </div>
        </div>
      )}

      {/* Engine badge & instructions */}
      {!loading && (
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
          <p className="text-center text-xs text-white/90">
            Posicione o código de barras dentro da área verde
          </p>
          <p className="text-center text-[10px] text-white/60 mt-0.5">
            {engine === 'native' ? '⚡ Decodificador nativo' : '📷 Decodificador ZXing'}
          </p>
        </div>
      )}
    </div>
  );
};
